<template>
  <div class="bg-white w-100pc h-100pc">
    <v-container
      v-if="loaded"
      class="mxw-800 mx-auto py-12"
    >
      <div
        ref="progressBar"
        :aria-label="progressBarAriaLabel"
        class="px-4 focus-invisible"
        role="progressbar"
        tabindex="0"
        aria-live
      >
        <v-progress-linear
          v-model="progress"
          :indeterminate="!loaded"
          class="focus-invisible mb-4"
          color="primary"
        />
      </div>

      <div class="px-4">
        <QuestionSet
          v-model="form"
          @back="backFromQuestion($event)"
          @change:attachments="loadAttachments"
          @next="forwardFromQuestion($event)"
          :answer-validation-complete="stableValidationComplete"
          :attachment-owner-id="form.id"
          :attachment-owner-type="'Form'"
          :attachments="attachments"
          :processing="processing"
          :questions="validPublishedQuestions"
          :readonly="form.locked"
          :schema="formSchema.definition"
          :section="section"
          :transition-name="transitionName"
          key-name="question"
        />
      </div>
    </v-container>
  </div>
</template>

<script setup>
import QuestionSet from '@/shared/components/form/QuestionSet.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import useQuestionable from '@/shared/composables/useQuestionable';
import { useRoute, useRouter } from 'vue-router';
import useRouterHelper from '@/shared/composables/useRouterHelper';
import useStepper from '@/shared/composables/useStepper';
import { useStore } from 'vuex';

const { api } = useApi();
const eventBus = useEventBus();
const route = useRoute();
const router = useRouter();
const { updateQuery } = useRouterHelper();
const store = useStore();
const $bct = inject('translator');

const attachments = ref([]);
const form = ref(null);
const formSchema = ref(null);
const processing = ref(false);
const progressBar = ref(null);

const formSchemaId = computed(() => route.params?.schemaId);

const { answerValidationComplete, questionsLoaded, validateAnswers, validPublishedQuestions } =
  useQuestionable({
    ownerDataTypeRef: ref('Schema'),
    ownerIdRef: formSchemaId,
    syncedObjectRef: form,
    skipValidationDuringProcessing: processing, // Prevent validation during navigation
  });

const stableValidationComplete = computed(() => {
  return questionsLoaded.value && answerValidationComplete.value && !processing.value;
});

const { progress, section, stepBack, stepCount, stepForward, stepTotal } = useStepper({
  processing,
  route,
  updateQuery,
});

const loaded = computed(() => {
  return (
    form.value &&
    formSchema.value &&
    questionsLoaded.value &&
    validPublishedQuestions.value.length > 0
  );
});

const progressBarAriaLabel = computed(() => {
  return `${$bct('Progress indicator - question')} ${stepCount.value}`;
});

watch(
  () => loaded.value,
  async (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      if (route.query.step) stepCount.value = parseInt(route.query.step, 10);
      else stepCount.value = 0;

      if (!stepTotal) await validateAnswers();

      recalculateStepTotal();

      if (route.query.section) {
        section.value = route.query.section;
        progressBar.value?.focus();
        progress.value = (stepCount.value / stepTotal) * 100;
      } else {
        start();
      }
    }
  },
  { immediate: true },
);

watch(
  () => questionsLoaded.value,
  (newVal) => {
    if (newVal) recalculateStepTotal();
  },
);

watch(
  () => route.query.section,
  async (newVal) => {
    const targetSection = newVal || 'question-0';

    // Skip if section is already set to avoid double updates from useStepper
    if (section.value === targetSection && !processing.value) return;

    // Set processing state during navigation
    processing.value = true;

    stepCount.value = parseInt(route.query.step, 10);

    if (!stepTotal.value) await validateAnswers();

    recalculateStepTotal();

    // Only update section when everything is ready
    section.value = targetSection;
    if (newVal) {
      progressBar.value?.focus();
    }
    progress.value = (stepCount.value / stepTotal.value) * 100;
    processing.value = false;
  },
);

onMounted(load);

async function load() {
  formSchema.value = store.state.schemas[route.params.schemaId];

  await loadForm();
  void loadAttachments();
}

async function loadForm() {
  const { data } = await api.parent.form.get(route.params.formId);
  form.value = data;
}

async function loadAttachments() {
  const params = {
    owner_type: 'Form',
    owner_id: form.value.id,
  };
  const response = await api.member.attachment.index(params);
  if (response?.status !== 200) return;
  attachments.value = response.data;
}

async function saveForm() {
  const { data } = await api.parent.form.update(form.value.id, form.value);
  form.value = data;
}

async function backFromQuestion(index) {
  processing.value = true;
  if (!form.value.locked) {
    await saveForm();
  }
  recalculateStepTotal();

  if (index - 1 < 0) {
    router.push({ name: route.query.backTo });
  } else {
    stepBack(`question-${index - 1}`);
  }
}

async function finish() {
  processing.value = true;
  if (!form.value.locked) {
    form.value.submitted = true;
    await api.parent.form.update(form.value.id, form.value);
    eventBus.chime('Form submitted');
  }
  router.push({ name: route.query.backTo });
}

async function forwardFromQuestion(index) {
  processing.value = true;
  if (!form.value.locked) {
    await saveForm();
  }
  recalculateStepTotal();

  if (index + 1 >= validPublishedQuestions.value.length) {
    void finish();
  } else {
    stepForward(`question-${index + 1}`);
  }
}

function recalculateStepTotal() {
  stepTotal.value = validPublishedQuestions.value.length;
  progress.value = (stepCount.value / stepTotal.value) * 100;
}

function start() {
  processing.value = true;
  if (validPublishedQuestions.value.length > 0) {
    stepForward('question-0');
  } else {
    void finish();
  }
}
</script>
